import {
  CP_EXCLUSIONS,
  CP_TYPES,
  FUEL_TYPES,
  PASSAGE_CLASSES,
  type PassageClass,
} from '@felix/domain';
import {
  Button,
  Checkbox,
  Modal,
  NumberInput,
  Select,
  Skeleton,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconChartBar,
  IconEdit,
  IconFileCertificate,
  IconInfoCircle,
  IconPlus,
  IconRoute,
  IconTag,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { DateTimeInput } from '../../inputs/DateTimeInput.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import classes from './CharterPartyScreens.module.css';
import {
  CP_STATUS_TONE,
  type CpFormValues,
  charterWithoutCp,
  cpContentOf,
  cpFormErrors,
  cpFormValues,
  hireReportLabel,
  newCpPrefill,
  PASSAGE_CLASS_TONE,
  unlinkedOnHire,
} from './charter-party.js';
import { ScrollTable } from './fields.js';
import { useNumber } from './numbers.js';
import type { VoyageSource } from './source.js';
import type {
  CharterPartyItem,
  CharterPartyPeriodView,
  CharterPartyRegisterData,
  HireReport,
  PassageClassView,
  VoyageReport,
} from './types.js';

type Message = { tone: 'success' | 'danger'; text: string } | null;

/** Ekranın yükleme durumu; görünüm dışarıdan beslenir (test edilebilir). */
export type CharterPartyLoad =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      vessels: PersonnelVessel[];
      vessel: PersonnelVessel | null;
      register: CharterPartyRegisterData;
    };

const EMPTY_REGISTER: CharterPartyRegisterData = { items: [], passages: [], hireReports: [] };

function useCpText() {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.charterParties.${key}`, options);
  const errorText = (error: unknown) => {
    const code = typeof error === 'string' ? error : errorCode(error);
    return t(`voyage.charterParties.errors.${code}`, {
      defaultValue: t('voyage.charterParties.errors.GENERIC'),
    });
  };
  return { t, k, errorText };
}

/** Dönemin bir ucu: an (UTC) ve dayanağı; yoksa "Açık" ya da "—". */
function PeriodEnd({
  at,
  basis,
  voyageNo,
  open,
}: {
  at: string | null;
  basis: CharterPartyPeriodView['startBasis'];
  voyageNo: string | null;
  open?: boolean;
}) {
  const { k } = useCpText();
  if (!at) return <>{open ? k('open') : '—'}</>;
  return (
    <span className={classes.primary}>
      {`${formatDateTime(at, 0)} UTC`}
      {basis ? (
        <span className={classes.sub}>{k(`basis.${basis}`, { no: voyageNo ?? '—' })}</span>
      ) : null}
    </span>
  );
}

const hours = (from: string, to: string | null) =>
  to ? (Date.parse(to) - Date.parse(from)) / 3_600_000 : null;

export interface CharterPartyRegisterViewProps {
  load: CharterPartyLoad;
  scope: 'web' | 'ship';
  /** Ofiste `voyage.edit` ve yazma kaynağı var. */
  canEdit: boolean;
  message?: Message;
  onVessel: (id: string) => void;
  onRetry: () => void;
  onNew: () => void;
  onEdit: (item: CharterPartyItem) => void;
  onRemove: (item: CharterPartyItem) => void;
  onClassify: (passage: PassageClassView) => void;
  onPerformance: (item: CharterPartyItem) => void;
  /** "Kalkışta kira bildirildi, CP kaydı yok" (ofis; ADR 0157 §7). */
  missing?: readonly { report: VoyageReport; reference: string | null }[];
  /** CP'ye bağlı olmayan On Hire raporları (ofis; ADR 0157 §7). */
  unlinkedHire?: readonly HireReport[];
  /** Kalkıştan ön doldurulmuş "Yeni CP". */
  onNewFrom?: (report: VoyageReport) => void;
}

/**
 * Charter Party Register görünümü (ADR 0084 §7): CP'ler (dönem, dayanak, durum, kaynak, örtüşme)
 * ve çarter dönemi dışındaki seyirler. Gemide salt okunur.
 */
export function CharterPartyRegisterView({
  load,
  scope,
  canEdit,
  message = null,
  onVessel,
  onRetry,
  onNew,
  onEdit,
  onRemove,
  onClassify,
  onPerformance,
  missing = [],
  unlinkedHire = [],
  onNewFrom,
}: CharterPartyRegisterViewProps) {
  const { t, k } = useCpText();
  const n = useNumber();
  const ready = load.kind === 'ready' ? load : null;
  const register = ready?.register ?? EMPTY_REGISTER;
  const cpNo = (id: string) => register.items.find((i) => i.id === id)?.cpNo ?? '—';

  return (
    <div className={classes.layout}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconFileCertificate size={28} stroke={1.6} />}
        actions={
          canEdit ? (
            <div className={classes.headerActions}>
              <Button
                leftSection={<IconPlus size={16} />}
                disabled={!ready?.vessel}
                onClick={onNew}
              >
                {k('new')}
              </Button>
            </div>
          ) : undefined
        }
      />

      {scope === 'web' ? (
        <Panel flush>
          <form
            className={classes.filters}
            aria-label={k('vessel')}
            onSubmit={(event) => event.preventDefault()}
          >
            <Select
              label={k('vessel')}
              aria-label={t('shell.selectVessel')}
              data={(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name }))}
              value={ready?.vessel?.id ?? null}
              allowDeselect={false}
              placeholder={k('noVessel')}
              onChange={(value) => value && onVessel(value)}
            />
          </form>
        </Panel>
      ) : (
        <div className={classes.notice} role="note">
          <IconInfoCircle size={16} aria-hidden />
          <span>{k('shipNote')}</span>
        </div>
      )}

      {message ? (
        <p
          className={classes.message}
          data-tone={message.tone}
          role={message.tone === 'danger' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      ) : null}

      {canEdit && (missing.length > 0 || unlinkedHire.length > 0) ? (
        <Panel
          title={t('voyage.context.charter.missingTitle')}
          icon={<IconInfoCircle size={20} />}
          divider
        >
          {missing.length > 0 ? (
            <>
              <p className={classes.muted}>{t('voyage.context.charter.missingDescription')}</p>
              <ul className={classes.hintList}>
                {missing.map(({ report, reference }) => (
                  <li key={report.id}>
                    <span>
                      {[
                        report.reportNo,
                        `${formatDateTime(report.reportedAt, 0)} UTC`,
                        reference ?? '—',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {onNewFrom ? (
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => onNewFrom(report)}
                      >
                        {t('voyage.context.charter.newFrom')}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {unlinkedHire.length > 0 ? (
            <>
              <p className={classes.muted}>{t('voyage.context.charter.onHireDescription')}</p>
              <ul className={classes.hintList}>
                {unlinkedHire.map((hire) => (
                  <li key={hire.id}>
                    <span>{hireReportLabel(hire)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </Panel>
      ) : null}

      <Panel title={k('list')} icon={<IconFileCertificate size={20} />} flush divider>
        {load.kind === 'loading' ? (
          <div className={classes.form} style={{ padding: 16 }}>
            <Skeleton height={36} radius="sm" />
            <Skeleton height={36} radius="sm" />
            <Skeleton height={36} radius="sm" />
          </div>
        ) : load.kind === 'error' ? (
          <EmptyState
            tone="danger"
            title={k('error')}
            action={
              <Button variant="default" onClick={onRetry}>
                {k('retry')}
              </Button>
            }
          />
        ) : register.items.length === 0 ? (
          <EmptyState
            icon={<IconFileCertificate size={22} stroke={1.8} />}
            title={k('empty')}
            description={canEdit ? k('emptyOffice') : k('voyageNote')}
          />
        ) : (
          <ScrollTable label={k('list')} framed={false}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th scope="col">{k('columns.cpNo')}</th>
                  <th scope="col">{k('columns.charterer')}</th>
                  <th scope="col">{k('columns.delivery')}</th>
                  <th scope="col">{k('columns.redelivery')}</th>
                  <th scope="col">{k('columns.status')}</th>
                  <th scope="col">{k('columns.source')}</th>
                  <th scope="col">{k('columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {register.items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <span className={classes.primary}>
                        {item.cpNo ?? '—'}
                        <span className={classes.sub}>
                          {k(`types.${item.cpType}`)}
                          {item.cpDate ? ` · ${formatDay(item.cpDate)}` : ''}
                        </span>
                        {item.overlaps.length > 0 ? (
                          <span
                            title={k('overlapHint', { list: item.overlaps.map(cpNo).join(', ') })}
                          >
                            <StatusBadge tone="warning" withIcon>
                              {k('overlap')}
                            </StatusBadge>
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td>
                      <span className={classes.primary}>
                        {item.charterer ?? '—'}
                        {item.terms.deliveryPlace ? (
                          <span className={classes.sub}>{item.terms.deliveryPlace}</span>
                        ) : null}
                      </span>
                    </td>
                    <td>
                      <PeriodEnd
                        at={item.period.start}
                        basis={item.period.startBasis}
                        voyageNo={item.voyageNo}
                      />
                    </td>
                    <td>
                      <PeriodEnd
                        at={item.period.end}
                        basis={item.period.endBasis}
                        voyageNo={item.voyageNo}
                        open={item.period.start !== null}
                      />
                    </td>
                    <td>
                      <StatusBadge tone={CP_STATUS_TONE[item.period.status]} variant="dot">
                        {k(`status.${item.period.status}`)}
                      </StatusBadge>
                    </td>
                    <td>{k(`sourceLabel.${item.source}`, { no: item.voyageNo ?? '—' })}</td>
                    <td>
                      <div className={classes.rowActions}>
                        <Button
                          size="xs"
                          variant="subtle"
                          leftSection={<IconChartBar size={14} />}
                          aria-label={`${k('performance')} ${item.cpNo ?? ''}`.trim()}
                          onClick={() => onPerformance(item)}
                        >
                          {k('performance')}
                        </Button>
                        {canEdit ? (
                          <>
                            <Button
                              size="xs"
                              variant="subtle"
                              leftSection={<IconEdit size={14} />}
                              aria-label={k('editTitle', { no: item.cpNo ?? '' })}
                              onClick={() => onEdit(item)}
                            >
                              {k('edit')}
                            </Button>
                            <Button
                              size="xs"
                              variant="subtle"
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              aria-label={k('removeTitle', { no: item.cpNo ?? '' })}
                              onClick={() => onRemove(item)}
                            >
                              {k('remove')}
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollTable>
        )}
      </Panel>

      {load.kind === 'ready' ? (
        <Panel title={k('passages.title')} icon={<IconRoute size={20} />} flush divider>
          <p className={classes.lead} style={{ marginTop: 12 }}>
            {k('passages.description')}
          </p>
          {register.passages.length === 0 ? (
            <EmptyState icon={<IconRoute size={22} stroke={1.8} />} title={k('passages.empty')} />
          ) : (
            <ScrollTable label={k('passages.title')} framed={false}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th scope="col">{k('passages.start')}</th>
                    <th scope="col">{k('passages.end')}</th>
                    <th scope="col" data-numeric>
                      {k('passages.hours')}
                    </th>
                    <th scope="col">{k('passages.suggested')}</th>
                    <th scope="col">{k('passages.classification')}</th>
                    <th scope="col">{k('passages.decision')}</th>
                    {canEdit ? <th scope="col">{k('columns.actions')}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {register.passages.map((passage) => (
                    <tr key={passage.id}>
                      <td>{`${formatDateTime(passage.start, 0)}`}</td>
                      <td>
                        {passage.end
                          ? formatDateTime(passage.end, 0)
                          : passage.ongoing
                            ? k('passages.ongoing')
                            : '—'}
                      </td>
                      <td data-numeric>
                        {n(
                          hours(
                            passage.start,
                            passage.end ?? (passage.ongoing ? null : passage.lastAt),
                          ),
                          {
                            digits: 1,
                            fixed: true,
                          },
                        )}
                      </td>
                      <td>{k(`passages.classes.${passage.suggested}`)}</td>
                      <td>
                        <StatusBadge tone={PASSAGE_CLASS_TONE[passage.classification]}>
                          {k(`passages.classes.${passage.classification}`)}
                        </StatusBadge>
                      </td>
                      <td>
                        {passage.decision ? (
                          <span className={classes.primary} style={{ fontWeight: 400 }}>
                            {passage.decision.reason}
                            <span className={classes.sub}>
                              {k('passages.by', {
                                name: passage.decision.createdByName ?? '—',
                                at: formatDateTime(passage.decision.createdAt, 0),
                              })}
                            </span>
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      {canEdit ? (
                        <td>
                          <Button
                            size="xs"
                            variant="subtle"
                            leftSection={<IconTag size={14} />}
                            aria-label={k('passages.classifyTitle', {
                              at: formatDateTime(passage.start, 0),
                            })}
                            onClick={() => onClassify(passage)}
                          >
                            {k('passages.classify')}
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollTable>
          )}
        </Panel>
      ) : null}
    </div>
  );
}

/**
 * Voyage ▸ Performance & Analysis ▸ Charter Party Register (ADR 0084 §7). Ofiste CP yazılır,
 * düzeltilir, kaldırılır ve dönem dışı seyir sınıflanır; gemide salt okunur.
 */
export function CharterPartyRegisterScreen({ source }: { source: VoyageSource }) {
  const shell = useShell();
  const { k, errorText } = useCpText();
  const [vesselId, setVesselId] = useState<string | null>(() =>
    new URLSearchParams(shell?.search ?? '').get('vessel'),
  );
  const [load, setLoad] = useState<CharterPartyLoad>({ kind: 'loading' });
  const [message, setMessage] = useState<Message>(null);
  const [editing, setEditing] = useState<CharterPartyItem | 'new' | null>(null);
  const [removing, setRemoving] = useState<CharterPartyItem | null>(null);
  const [classifying, setClassifying] = useState<PassageClassView | null>(null);
  const [busy, setBusy] = useState(false);
  // Ofiste geminin raporları: "Kiralı" kalkış ve CP kaydı yok listesi (ADR 0157 §7)
  const [reports, setReports] = useState<VoyageReport[]>([]);
  const [prefill, setPrefill] = useState<CpFormValues | null>(null);
  const editor = source.charterPartyEditor;

  const read = useCallback(async () => {
    setLoad({ kind: 'loading' });
    try {
      const vessels = await source.vessels();
      const vessel = vessels.find((v) => v.id === vesselId) ?? vessels[0] ?? null;
      const register =
        vessel && source.charterParties ? await source.charterParties(vessel.id) : EMPTY_REGISTER;
      // Liste yalnız bilgi: okunamazsa boş kalır, register yine açılır
      setReports(
        vessel && source.charterPartyEditor && source.allReports
          ? await source.allReports(vessel.id).catch(() => [])
          : [],
      );
      setLoad({ kind: 'ready', vessels, vessel, register });
    } catch (error) {
      setLoad({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void read();
  }, [read]);

  const ready = load.kind === 'ready' ? load : null;
  const canEdit = !!editor && (ready?.register.can?.edit ?? false);
  const vessel = ready?.vessel ?? null;

  const remove = async () => {
    if (!editor || !removing) return;
    setBusy(true);
    try {
      await editor.remove(removing.id, removing.rowVersion);
      setRemoving(null);
      setMessage({ tone: 'success', text: k('removed') });
      await read();
    } catch (error) {
      setMessage({ tone: 'danger', text: errorText(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <CharterPartyRegisterView
        load={load}
        scope={source.scope}
        canEdit={canEdit}
        message={message}
        onVessel={(id) => {
          setMessage(null);
          setVesselId(id);
        }}
        onRetry={() => void read()}
        onNew={() => {
          setPrefill(null);
          setEditing('new');
        }}
        missing={canEdit ? charterWithoutCp(reports, ready?.register.items ?? []) : []}
        unlinkedHire={
          canEdit
            ? unlinkedOnHire(ready?.register.hireReports ?? [], ready?.register.items ?? [])
            : []
        }
        onNewFrom={(report) => {
          setPrefill(newCpPrefill(report));
          setEditing('new');
        }}
        onEdit={(item) => setEditing(item)}
        onRemove={(item) => setRemoving(item)}
        onClassify={(passage) => setClassifying(passage)}
        onPerformance={(item) =>
          shell?.navigate(`/voyage/charter-party?vessel=${item.vesselId}&cp=${item.id}`)
        }
      />
      {editor && vessel ? (
        <CharterPartyFormModal
          opened={editing !== null}
          item={editing === 'new' ? null : editing}
          initial={editing === 'new' ? prefill : null}
          hireReports={ready?.register.hireReports ?? []}
          onClose={() => setEditing(null)}
          onSave={async (content, existing) => {
            await editor.save(vessel.id, content, existing);
            setEditing(null);
            setMessage({ tone: 'success', text: k('saved') });
            await read();
          }}
        />
      ) : null}
      {editor && vessel ? (
        <ClassifyPassageModal
          passage={classifying}
          onClose={() => setClassifying(null)}
          onSave={async (classification, reason, reference) => {
            if (!classifying) return;
            await editor.decide(vessel.id, {
              charterPartyId: null,
              action: 'classify',
              targetKind: 'passage',
              targetId: classifying.id,
              classification,
              reason,
              reference,
            });
            setClassifying(null);
            setMessage({ tone: 'success', text: k('passages.classified') });
            await read();
          }}
        />
      ) : null}
      <Modal
        opened={removing !== null}
        onClose={() => setRemoving(null)}
        title={k('removeTitle', { no: removing?.cpNo ?? '' })}
        closeButtonProps={{ 'aria-label': k('cancel') }}
      >
        <p className={classes.hint}>{k('removeConfirm')}</p>
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={() => setRemoving(null)}>
            {k('cancel')}
          </Button>
          <Button color="red" loading={busy} onClick={() => void remove()}>
            {k('remove')}
          </Button>
        </div>
      </Modal>
    </>
  );
}

/** Sayı alanı: boş değer `null`. */
function NumberBox({
  label,
  value,
  onChange,
  error,
  decimals = 1,
  max,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  error?: string | undefined;
  decimals?: number;
  max?: number;
}) {
  return (
    <NumberInput
      label={label}
      value={value ?? ''}
      min={0}
      {...(max !== undefined ? { max } : {})}
      decimalScale={decimals}
      allowNegative={false}
      error={error}
      onChange={(next) => onChange(next === '' || next === null ? null : Number(next))}
    />
  );
}

/**
 * CP formu (ADR 0084 §7): Contract, Delivery & Redelivery, Speed & Consumption, Weather Limits,
 * Exclusions, Notes. Doğrulama sunucudaki şemayla aynı; sunucunun reddi de alan altında görünür.
 */
export function CharterPartyFormModal({
  opened,
  item,
  initial = null,
  hireReports,
  onClose,
  onSave,
}: {
  opened: boolean;
  item: CharterPartyItem | null;
  /** Yeni CP'nin ön doldurması (kalkıştan; ADR 0157 §7); düzenlemede yok sayılır. */
  initial?: CpFormValues | null;
  hireReports: readonly HireReport[];
  onClose: () => void;
  onSave: (content: unknown, existing?: { id: string; rowVersion: number }) => Promise<void>;
}) {
  const { t, k, errorText } = useCpText();
  const f = (key: string) => k(`form.${key}`);
  const [values, setValues] = useState<CpFormValues>(() =>
    item ? cpFormValues(item) : (initial ?? cpFormValues(null)),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setValues(item ? cpFormValues(item) : (initial ?? cpFormValues(null)));
    setErrors({});
    setFailure(null);
  }, [opened, item, initial]);

  const set =
    <K extends keyof CpFormValues>(key: K) =>
    (value: CpFormValues[K]) =>
      setValues((current) => ({ ...current, [key]: value }));
  const error = (key: string) => (errors[key] ? errorText(errors[key]) : undefined);

  const submit = async () => {
    const found = cpFormErrors(values);
    setErrors(found);
    setFailure(null);
    if (Object.keys(found).length > 0) {
      setFailure(errorText('INVALID_CHARTER_PARTY'));
      return;
    }
    setBusy(true);
    try {
      await onSave(
        cpContentOf(values),
        item ? { id: item.id, rowVersion: item.rowVersion } : undefined,
      );
    } catch (failed) {
      setFailure(errorText(failed));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={item ? k('editTitle', { no: item.cpNo ?? '' }) : f('newTitle')}
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <form
        className={classes.form}
        aria-label={item ? k('editTitle', { no: item.cpNo ?? '' }) : f('newTitle')}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <CharterPartyFields values={values} set={set} error={error} hireReports={hireReports} />

        {failure ? (
          <p className={classes.message} data-tone="danger" role="alert">
            {failure}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {t('voyage.charterParties.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * CP formunun alanları (ADR 0084 §7): register'ın formu ve Add New Voyage'ın "New charter party"
 * seçeneği aynı alanları kullanır (ADR 0084 Ek 2; tek form). `withFuel` kapalıysa yakıtlar
 * seferin yakıt alanlarından gelir.
 */
export function CharterPartyFields({
  values,
  set,
  error,
  hireReports,
  withFuel = true,
}: {
  values: CpFormValues;
  set: <K extends keyof CpFormValues>(key: K) => (value: CpFormValues[K]) => void;
  error: (key: string) => string | undefined;
  hireReports: readonly HireReport[];
  withFuel?: boolean;
}) {
  const { k } = useCpText();
  const f = (key: string) => k(`form.${key}`);
  const options = (kind: HireReport['kind']) => [
    { value: '', label: f('noReport') },
    ...hireReports
      .filter((r) => r.kind === kind)
      .map((r) => ({ value: r.id, label: hireReportLabel(r) })),
  ];
  const fuel = FUEL_TYPES.map((value) => ({ value, label: value }));
  return (
    <>
      <fieldset className={classes.section}>
        <legend>{f('contract')}</legend>
        <div className={classes.grid}>
          <TextInput
            label={f('charterer')}
            required
            maxLength={200}
            value={values.charterer}
            error={error('charterer')}
            onChange={(event) => set('charterer')(event.currentTarget.value)}
          />
          <TextInput
            label={f('cpNo')}
            required
            maxLength={60}
            value={values.cpNo}
            error={error('cpNo')}
            onChange={(event) => set('cpNo')(event.currentTarget.value)}
          />
          <DayInput label={f('cpDate')} value={values.cpDate} onChange={set('cpDate')} />
          <Select
            label={f('cpType')}
            data={CP_TYPES.map((value) => ({ value, label: k(`types.${value}`) }))}
            value={values.cpType}
            allowDeselect={false}
            onChange={(value) => value && set('cpType')(value as CpFormValues['cpType'])}
          />
        </div>
      </fieldset>

      <fieldset className={classes.section}>
        <legend>{f('delivery')}</legend>
        <p className={classes.hint}>{f('deliveryNote')}</p>
        <div className={classes.grid}>
          <TextInput
            label={f('deliveryPlace')}
            maxLength={200}
            value={values.deliveryPlace}
            onChange={(event) => set('deliveryPlace')(event.currentTarget.value)}
          />
          <DateTimeInput
            label={f('deliveryAt')}
            value={values.deliveryAt}
            error={error('deliveryAt')}
            onChange={set('deliveryAt')}
            // Yer tutucu metni okunur karşıtlıkta (WCAG 1.4.3; sefer formuyla aynı)
            classNames={{ placeholder: classes.placeholder ?? '' }}
          />
          <Select
            label={f('deliveryReport')}
            data={options('on_hire')}
            value={values.deliveryReportId ?? ''}
            allowDeselect={false}
            error={error('deliveryReportId')}
            onChange={(value) => set('deliveryReportId')(value ? value : null)}
          />
          <TextInput
            label={f('redeliveryPlace')}
            maxLength={200}
            value={values.redeliveryPlace}
            onChange={(event) => set('redeliveryPlace')(event.currentTarget.value)}
          />
          <DateTimeInput
            label={f('redeliveryAt')}
            value={values.redeliveryAt}
            error={error('redeliveryAt')}
            onChange={set('redeliveryAt')}
            // Yer tutucu metni okunur karşıtlıkta (WCAG 1.4.3; sefer formuyla aynı)
            classNames={{ placeholder: classes.placeholder ?? '' }}
          />
          <Select
            label={f('redeliveryReport')}
            data={options('off_hire')}
            value={values.redeliveryReportId ?? ''}
            allowDeselect={false}
            error={error('redeliveryReportId')}
            onChange={(value) => set('redeliveryReportId')(value ? value : null)}
          />
        </div>
      </fieldset>

      <fieldset className={classes.section}>
        <legend>{f('speed')}</legend>
        <div className={classes.grid}>
          <NumberBox
            label={f('speedLaden')}
            value={values.speedLadenKn}
            error={error('speedLadenKn')}
            onChange={set('speedLadenKn')}
          />
          <NumberBox
            label={f('speedBallast')}
            value={values.speedBallastKn}
            error={error('speedBallastKn')}
            onChange={set('speedBallastKn')}
          />
          <NumberBox
            label={f('consumptionLaden')}
            value={values.consumptionLadenMt}
            error={error('consumptionLadenMt')}
            onChange={set('consumptionLadenMt')}
          />
          <NumberBox
            label={f('consumptionBallast')}
            value={values.consumptionBallastMt}
            error={error('consumptionBallastMt')}
            onChange={set('consumptionBallastMt')}
          />
          <NumberBox
            label={f('speedTolerance')}
            value={values.speedToleranceKn}
            error={error('speedToleranceKn')}
            onChange={set('speedToleranceKn')}
          />
          <NumberBox
            label={f('consumptionTolerance')}
            value={values.consumptionTolerancePct}
            error={error('consumptionTolerancePct')}
            onChange={set('consumptionTolerancePct')}
          />
          {withFuel ? (
            <>
              <Select
                label={f('mainFuel')}
                data={fuel}
                value={values.mainFuel}
                clearable
                onChange={set('mainFuel')}
              />
              <Select
                label={f('auxFuel')}
                data={fuel}
                value={values.auxFuel}
                clearable
                onChange={set('auxFuel')}
              />
            </>
          ) : null}
        </div>
      </fieldset>

      <fieldset className={classes.section}>
        <legend>{f('weather')}</legend>
        <div className={classes.grid}>
          <NumberBox
            label={f('maxBeaufort')}
            value={values.maxBeaufort}
            decimals={0}
            max={12}
            error={error('maxBeaufort')}
            onChange={set('maxBeaufort')}
          />
          <NumberBox
            label={f('maxDouglas')}
            value={values.maxDouglas}
            decimals={0}
            max={9}
            error={error('maxDouglas')}
            onChange={set('maxDouglas')}
          />
          <NumberBox
            label={f('maxWaveHeight')}
            value={values.maxWaveHeightM}
            error={error('maxWaveHeightM')}
            onChange={set('maxWaveHeightM')}
          />
          <NumberBox
            label={f('maxSwell')}
            value={values.maxSwellM}
            error={error('maxSwellM')}
            onChange={set('maxSwellM')}
          />
          <NumberBox
            label={f('minDayHours')}
            value={values.minDayHours}
            max={24}
            error={error('minDayHours')}
            onChange={set('minDayHours')}
          />
          <TextInput
            label={f('currentClause')}
            maxLength={300}
            value={values.currentClause}
            onChange={(event) => set('currentClause')(event.currentTarget.value)}
          />
        </div>
      </fieldset>

      <fieldset className={classes.section}>
        <legend>{f('exclusions')}</legend>
        <p className={classes.hint}>{f('exclusionsHint')}</p>
        <Checkbox.Group
          aria-label={f('exclusions')}
          value={values.exclusions}
          onChange={(next) => set('exclusions')(next as CpFormValues['exclusions'])}
        >
          <div className={classes.grid}>
            {CP_EXCLUSIONS.map((value) => (
              <Checkbox key={value} value={value} label={f(`exclusion.${value}`)} />
            ))}
          </div>
        </Checkbox.Group>
        <Textarea
          label={f('notes')}
          maxLength={2000}
          autosize
          minRows={2}
          value={values.notes}
          onChange={(event) => set('notes')(event.currentTarget.value)}
        />
      </fieldset>
    </>
  );
}

/** Dönem dışı seyrin sınıfı: öneri seçili gelir; gerekçe zorunlu (değiştirilemez iz). */
export function ClassifyPassageModal({
  passage,
  onClose,
  onSave,
}: {
  passage: PassageClassView | null;
  onClose: () => void;
  onSave: (classification: PassageClass, reason: string, reference: string | null) => Promise<void>;
}) {
  const { k, errorText } = useCpText();
  const [classification, setClassification] = useState<PassageClass>('unclassified');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!passage) return;
    setClassification(passage.decided ? passage.classification : passage.suggested);
    setReason('');
    setReference('');
    setFailure(null);
  }, [passage]);
  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await onSave(classification, reason.trim(), reference.trim() || null);
    } catch (error) {
      setFailure(errorText(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      opened={passage !== null}
      onClose={onClose}
      title={k('passages.classifyTitle', {
        at: passage ? formatDateTime(passage.start, 0) : '',
      })}
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <div className={classes.form}>
        <Select
          label={k('passages.classification')}
          data={PASSAGE_CLASSES.map((value) => ({
            value,
            label: k(`passages.classes.${value}`),
          }))}
          value={classification}
          allowDeselect={false}
          onChange={(value) => value && setClassification(value as PassageClass)}
        />
        <Textarea
          label={k('passages.reason')}
          description={k('passages.reasonHint')}
          required
          maxLength={1000}
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
        <TextInput
          label={k('passages.reference')}
          description={k('passages.referenceHint')}
          maxLength={1000}
          value={reference}
          onChange={(event) => setReference(event.currentTarget.value)}
        />
        {failure ? (
          <p className={classes.message} data-tone="danger" role="alert">
            {failure}
          </p>
        ) : null}
      </div>
      <div className={classes.dialogActions}>
        <Button variant="default" onClick={onClose}>
          {k('cancel')}
        </Button>
        <Button loading={busy} disabled={!reason.trim()} onClick={() => void save()}>
          {k('passages.classify')}
        </Button>
      </div>
    </Modal>
  );
}

export function CpNotice({ children, tone }: { children: ReactNode; tone?: 'warning' }) {
  return (
    <div className={classes.notice} data-tone={tone} role="note">
      {tone === 'warning' ? (
        <IconAlertTriangle size={16} aria-hidden />
      ) : (
        <IconInfoCircle size={16} aria-hidden />
      )}
      <span>{children}</span>
    </div>
  );
}
